import React, { useCallback, useEffect, useState } from 'react';
import { ticketApi } from '../api/tickets';
import type { TicketEvent, TicketEventType } from '../types';

const STATUS_LABEL: Record<string, string> = {
  NEW: 'Nowy',
  IN_PROGRESS: 'W trakcie',
  AWAITING_CUSTOMER: 'Czeka na klienta',
  CLOSED: 'Zamknięte',
};

const PRIORITY_LABEL: Record<string, string> = {
  LOW: 'Niski',
  NORMAL: 'Normalny',
  HIGH: 'Wysoki',
  CRITICAL: 'Krytyczny',
};

const ICON: Record<TicketEventType, string> = {
  CREATED: '🆕',
  MESSAGE_ADDED: '💬',
  AI_REPLY: '🤖',
  AI_VERIFIED: '✅',
  STATUS_CHANGED: '🔁',
  PRIORITY_CHANGED: '⚡',
  ASSIGNED: '👤',
  UNASSIGNED: '🚫',
  AUTO_CLOSED: '⏳',
  REOPENED: '🔓',
  TAG_ADDED: '🏷️',
  TAG_REMOVED: '🏷️',
};

const renderText = (e: TicketEvent): string => {
  const actor = e.actorFirstName ? `${e.actorFirstName} ${e.actorLastName ?? ''}`.trim() : null;
  const payload: any = e.payloadJson ? safeParse(e.payloadJson) : {};
  switch (e.eventType) {
    case 'CREATED':
      return `${actor ?? 'Klient'} utworzył(a) zgłoszenie${payload?.title ? `: "${payload.title}"` : ''}`;
    case 'MESSAGE_ADDED':
      return `${actor ?? 'Użytkownik'} dodał(a) wiadomość`;
    case 'AI_REPLY':
      return `AI wygenerowało odpowiedź`;
    case 'AI_VERIFIED':
      return `${actor ?? 'Integrator'} potwierdził(a) odpowiedź AI`;
    case 'STATUS_CHANGED':
      return `${actor ?? 'System'} zmienił(a) status: ${labelOr(STATUS_LABEL, payload?.from)} → ${labelOr(STATUS_LABEL, payload?.to)}`;
    case 'PRIORITY_CHANGED':
      return `${actor ?? 'Integrator'} zmienił(a) priorytet: ${labelOr(PRIORITY_LABEL, payload?.from)} → ${labelOr(PRIORITY_LABEL, payload?.to)}`;
    case 'ASSIGNED':
      return `${actor ?? 'Administrator'} przypisał(a) zgłoszenie`;
    case 'UNASSIGNED':
      return `${actor ?? 'Administrator'} cofnął(ęła) przypisanie`;
    case 'AUTO_CLOSED':
      return `Zgłoszenie zamknięte automatycznie po ${payload?.daysInactive ?? '4'} dniach bez odpowiedzi klienta`;
    case 'REOPENED':
      return `${actor ?? 'Użytkownik'} otworzył(a) zgłoszenie ponownie`;
    case 'TAG_ADDED':
      return `${actor ?? 'Integrator'} dodał(a) tag "${payload?.name ?? ''}"`;
    case 'TAG_REMOVED':
      return `${actor ?? 'Integrator'} usunął(ęła) tag "${payload?.name ?? ''}"`;
    default:
      return e.eventType;
  }
};

const labelOr = (map: Record<string, string>, key: any): string =>
  (key && map[key]) || key || '?';

const safeParse = (s: string): any => {
  try { return JSON.parse(s); } catch { return {}; }
};

const TicketTimeline: React.FC<{ ticketId: string; refreshKey?: number }> = ({ ticketId, refreshKey }) => {
  const [events, setEvents] = useState<TicketEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await ticketApi.timeline(ticketId);
      setEvents(data);
      setError('');
    } catch (e: any) {
      setError('Nie udało się załadować historii');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) return <div className="empty-state" style={{ padding: '0.5rem' }}>Ładowanie historii…</div>;
  if (error) return <div className="auth-error">{error}</div>;
  if (events.length === 0) return <div className="empty-state" style={{ padding: '0.5rem' }}>Brak zdarzeń</div>;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '0.375rem',
      borderLeft: '2px solid var(--color-border)',
      paddingLeft: '0.875rem',
      marginLeft: '0.5rem',
    }}>
      {events.map((e) => (
        <div key={e.id} style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.625rem',
          position: 'relative',
          fontSize: '0.8125rem',
        }}>
          <span style={{
            position: 'absolute',
            left: -22,
            top: 2,
            width: 16, height: 16, borderRadius: '50%',
            background: 'var(--color-surface)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.6875rem',
          }}>
            {ICON[e.eventType] || '•'}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ color: 'var(--color-text)' }}>{renderText(e)}</div>
            <div className="item-meta" style={{ fontSize: '0.6875rem' }}>
              {new Date(e.createdAt).toLocaleString('pl-PL')}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TicketTimeline;
